// Which modules the firm has on, one card each. Switching one off makes
// its screens read-only and its writes refused; its records stay. Only a
// card with a switch can change here - the rest say in a word whether
// they are included in every bundle or still planned.

import { useState } from "react";
import { Link } from "react-router-dom";
import type { FirmModuleRow, FirmModulesUpdate } from "@duralex/shared";
import { useCurrentUser } from "../../components/auth/CurrentUserContext";
import { isAdminRole } from "../../lib/roles";
import { Skeleton, SkeletonGroup } from "../../components/chrome/Skeleton";
import { useFirmModules, useSaveFirmModules } from "../../hooks/useFirmModules";
import { SettingsNav } from "./SettingsNav";

const STATE_WORD: Record<Exclude<FirmModuleRow["state"], "switchable">, string> = {
  included: "Included",
  planned: "Planned",
};

export function ModulesSettings() {
  const user = useCurrentUser();
  const isAdmin = isAdminRole(user?.role);
  const modulesQ = useFirmModules(isAdmin);
  const save = useSaveFirmModules();
  const [edits, setEdits] = useState<FirmModulesUpdate>({});

  if (!isAdmin) {
    return (
      <main className="cc-main">
        <div className="cc-body settings-body">
          <SettingsNav />
          <section className="cc-card">
            <h3>Modules</h3>
            <p className="settings-sub">
              {user
                ? "Admin access is required to change the firm's modules."
                : "Could not confirm your account. Refresh to retry."}
            </p>
          </section>
        </div>
      </main>
    );
  }

  const data = modulesQ.data;
  const rows = data?.modules ?? [];
  // Only what differs: an unchanged entry still stamps the saved-by line,
  // and a switch turned back before Save is no change at all.
  const update: FirmModulesUpdate = {};
  for (const row of rows) {
    const wanted = edits[row.id];
    if (wanted !== undefined && wanted !== row.enabled) update[row.id] = wanted;
  }
  const dirty = Object.keys(update).length > 0;
  const busy = save.isPending;

  const live = rows.filter((r) => r.state !== "planned");
  const planned = rows.filter((r) => r.state === "planned");
  // The two-cell card leads its grid, so no cell is skipped before it.
  live.sort((a, b) => Number(Boolean(b.off)) - Number(Boolean(a.off)));

  function onToggle(id: FirmModuleRow["id"], on: boolean) {
    // A refusal is answered by the next edit, not carried under it.
    save.reset();
    setEdits((prev) => ({ ...prev, [id]: on }));
  }

  function onSave() {
    const goingOff = rows.filter((r) => update[r.id] === false);
    if (
      goingOff.length > 0 &&
      !window.confirm(
        `Switch off ${goingOff.map((r) => r.name).join(" and ")}?\n\n` +
          goingOff.map((r) => (r.off ? `${r.off.stops} ${r.off.stays}` : "")).join("\n\n"),
      )
    ) {
      return;
    }
    save.mutate(update, { onSuccess: () => setEdits({}) });
  }

  function moduleCard(row: FirmModuleRow) {
    const wanted = edits[row.id] ?? row.enabled;
    // A planned module answers "on" like every row without a switch, but
    // has no screens or switch yet: it stays dashed.
    const on = row.state !== "planned" && wanted;
    // A planned card sits under the Planned heading in the outline too.
    const Name = row.state === "planned" ? "h5" : "h4";
    return (
      <article
        key={row.id}
        className={"settings-module" + (on ? " on" : "") + (row.off ? " wide" : "")}
        aria-labelledby={`module-${row.id}`}
      >
        <div className="settings-module-head">
          <Name id={`module-${row.id}`} className="settings-module-name">
            {row.name}
          </Name>
          {row.state === "switchable" ? (
            <label className="settings-module-switch">
              <input
                type="checkbox"
                aria-label={row.name}
                checked={wanted}
                disabled={busy}
                onChange={(e) => onToggle(row.id, e.target.checked)}
              />
              <span className="settings-policy-switch" aria-hidden="true" />
            </label>
          ) : (
            <span className="settings-label">{STATE_WORD[row.state]}</span>
          )}
        </div>
        <span className="settings-label">{row.bundle} bundle</span>
        <p className="settings-module-text">{row.summary}</p>
        {row.off && (
          <dl className="settings-module-off">
            <dt className="settings-label">Stops when off</dt>
            <dd>{row.off.stops}</dd>
            <dt className="settings-label">Stays</dt>
            <dd>{row.off.stays}</dd>
          </dl>
        )}
        {row.connections.length > 0 && (
          <ul className="settings-module-links">
            {row.connections.map((name) => (
              <li key={name}>
                <Link className="cc-pill" to="/settings/integrations">
                  {name}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </article>
    );
  }

  return (
    <main className="cc-main">
      <div className="cc-body settings-body settings-wide">
        <SettingsNav />
        <header className="settings-head">
          <div>
            <h2>Modules</h2>
            <p className="settings-sub">
              What the firm has switched on. A module that is off keeps its records readable and
              refuses new work until it is on again. Included modules are part of every bundle and
              have no switch.
            </p>
          </div>
        </header>

        <section className="cc-card settings-section">
          <div className="settings-head">
            <div>
              <h3>Functional modules</h3>
              <p className="settings-sub">
                Connections and the client portal are on the Integrations tab.
              </p>
            </div>
            <div className="settings-save">
              <span className={"settings-status" + (save.isError ? " bad" : "")} role="status">
                {save.isPending
                  ? "Saving..."
                  : save.isError
                    ? (save.error as Error).message
                    : dirty
                      ? "Unsaved changes"
                      : save.isSuccess
                        ? "Saved"
                        : ""}
              </span>
              <button
                type="button"
                className="settings-save-btn"
                disabled={!dirty || busy}
                onClick={onSave}
              >
                Save changes
              </button>
            </div>
          </div>
          {/* isPending, not isLoading: offline the ask is parked, and the
              card must not stand empty over a disabled Save. */}
          {modulesQ.isPending && (
            <SkeletonGroup>
              <Skeleton width={220} />
              <Skeleton width={180} />
            </SkeletonGroup>
          )}
          {modulesQ.isError && (
            <p className="settings-error">Could not load the module settings. Refresh to retry.</p>
          )}
          {data && (
            <>
              <div className="settings-modules">{live.map(moduleCard)}</div>
              {planned.length > 0 && (
                <>
                  <h4 className="settings-label settings-modules-group">Planned</h4>
                  <p className="settings-hint">
                    Planned modules are not built and have no date; plans can change.
                  </p>
                  <div className="settings-modules">{planned.map(moduleCard)}</div>
                </>
              )}
              {data.source === "db" && data.updatedAt && (
                <p className="settings-hint">
                  Saved {new Date(data.updatedAt).toLocaleDateString()}
                  {data.updatedByEmail ? ` by ${data.updatedByEmail}` : ""}
                </p>
              )}
            </>
          )}
        </section>
      </div>
    </main>
  );
}
